import {
  DEFAULT_PREFERENCES,
  type LastContext,
  type UserPreferences
} from './types';
import { setLocale, type Locale } from '$core/i18n';
import type { Theme } from '$domain/types';
import type { SessionAction, ShortcutMap } from '$features/session/keys';
import { DEFAULT_SHORTCUTS } from '$features/session/keys';
import { getThemeById, THEME_TOKEN_KEYS } from '$ui/styles/themes.data';

const STORAGE_KEY = 'sq:preferences';

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const t = getThemeById(theme);
  const root = document.documentElement;

  // Data-driven injection: cada token del tema se aplica como CSS var
  // inline sobre <html>. Al ser inline, gana sobre cualquier stylesheet
  // y elimina la necesidad de archivos CSS por tema (refactor Monkeytype-style).
  for (const key of THEME_TOKEN_KEYS) {
    root.style.setProperty(`--${key}`, t.tokens[key]);
  }

  root.setAttribute('data-theme', theme);
  root.setAttribute('data-color-scheme', t.colorScheme);

  // Defensive reset: Modal/Drawer set `document.body.style.overflow = 'hidden'`
  // while open and restore it on close. If the theme change triggers a
  // rerender that unmounts a component before its cleanup $effect runs,
  // the body can get stuck unscrollable. Reset only when no modal is open.
  const modalOpen =
    document.querySelector('[role="dialog"][aria-modal="true"]') !== null ||
    document.querySelector('.backdrop') !== null;
  if (!modalOpen && document.body.style.overflow === 'hidden') {
    document.body.style.overflow = '';
  }
  if (!modalOpen && document.documentElement.style.overflow === 'hidden') {
    document.documentElement.style.overflow = '';
  }
}

export function applyInitialTheme(): void {
  const prefs = load();
  applyTheme(prefs.theme);
}

function load(): UserPreferences {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_PREFERENCES };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCES };
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed,
      shortcuts: { ...DEFAULT_SHORTCUTS, ...(parsed.shortcuts ?? {}) }
    };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export class PreferencesStore {
  prefs: UserPreferences = $state(load());

  constructor() {
    setLocale(this.prefs.locale);
  }

  setTheme(theme: Theme): void {
    this.prefs = { ...this.prefs, theme };
    applyTheme(theme);
    this.persist();
  }

  applyPreferences(next: UserPreferences): void {
    const prev = this.prefs;
    this.prefs = { ...next, shortcuts: { ...next.shortcuts } };
    if (prev.locale !== next.locale) setLocale(next.locale);
    if (prev.theme !== next.theme) applyTheme(next.theme);
    this.persist();
  }

  toggleBlindMode(): void {
    this.prefs = { ...this.prefs, blindMode: !this.prefs.blindMode };
    this.persist();
  }

  setLocale(locale: Locale): void {
    this.prefs = { ...this.prefs, locale };
    setLocale(locale);
    this.persist();
  }

  setShortcut(action: SessionAction, key: string): void {
    const shortcuts: ShortcutMap = { ...this.prefs.shortcuts };
    for (const other of Object.keys(shortcuts) as SessionAction[]) {
      if (other !== action && shortcuts[other] === key) {
        shortcuts[other] = '';
      }
    }
    shortcuts[action] = key;
    this.prefs = { ...this.prefs, shortcuts };
    this.persist();
  }

  resetShortcuts(): void {
    this.prefs = { ...this.prefs, shortcuts: { ...DEFAULT_SHORTCUTS } };
    this.persist();
  }

  setLastContext(ctx: LastContext | null): void {
    this.prefs = { ...this.prefs, lastContext: ctx };
    this.persist();
  }

  set<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]): void {
    this.prefs = { ...this.prefs, [key]: value };
    this.persist();
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.prefs));
    } catch {
      // localStorage full or unavailable
    }
  }
}